import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Restore as RestoreIcon,
  MoreVert as MoreVertIcon,
  Compare as CompareIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { ApiService } from '../../../services/ApiService';

interface Version {
  id: string | null;
  document_id: string;
  version: number;
  title: string;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  file_path: string;
  file_hash: string;
  type: string;
  requirement_id: string | null;
  metadata: any;
  version_notes: string;
  uploaded_by: string;
  created_at: string;
  uploader_name: string;
  uploader_email: string;
  is_current: boolean;
}

interface DocumentVersionHistoryProps {
  documentId: string;
  currentVersion?: number;
  onVersionRestore?: (version: Version) => void;
  onVersionCompare?: (fromVersion: number, toVersion: number) => void;
}

const DocumentVersionHistory: React.FC<DocumentVersionHistoryProps> = ({
  documentId,
  currentVersion = 1,
  onVersionRestore,
  onVersionCompare,
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [restoreNotes, setRestoreNotes] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVersion, setMenuVersion] = useState<Version | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareFrom, setCompareFrom] = useState<number | null>(null);

  useEffect(() => {
    fetchVersions();
  }, [documentId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.get<{ versions: Version[] }>(`/documents/${documentId}/versions`);
      setVersions(response.versions || []);
    } catch (err: any) {
      console.error('Error fetching versions:', err);
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVersion = async (version: Version) => {
    try {
      window.open(`/api/documents/${documentId}/versions/${version.version}/download`, '_blank');
    } catch (err: any) {
      console.error('Error downloading version:', err);
      setError(err.message || 'Failed to download version');
    }
  };

  const handleRestoreVersion = async () => {
    if (!selectedVersion || !selectedVersion.version) return;

    try {
      setRestoring(true);
      await ApiService.post(
        `/documents/${documentId}/versions/${selectedVersion.version}/restore`,
        { notes: restoreNotes || `Restored from version ${selectedVersion.version}` }
      );
      
      setRestoreDialogOpen(false);
      setSelectedVersion(null);
      setRestoreNotes('');
      
      // Refresh versions
      await fetchVersions();
      
      if (onVersionRestore) {
        onVersionRestore(selectedVersion);
      }
    } catch (err: any) {
      console.error('Error restoring version:', err);
      setError(err.message || 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteVersion = async (version: Version) => {
    if (!window.confirm(`Are you sure you want to archive version ${version.version}?`)) {
      return;
    }

    try {
      await ApiService.delete(`/documents/${documentId}/versions/${version.version}`);
      await fetchVersions();
    } catch (err: any) {
      console.error('Error deleting version:', err);
      setError(err.message || 'Failed to delete version');
    }
  };

  const handleCompareVersions = () => {
    if (compareFrom && menuVersion && onVersionCompare) {
      onVersionCompare(compareFrom, menuVersion.version);
      setCompareMode(false);
      setCompareFrom(null);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, version: Version) => {
    setAnchorEl(event.currentTarget);
    setMenuVersion(version);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuVersion(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Version History</Typography>
        {compareMode && (
          <Chip
            label={`Compare mode: Select version to compare with v${compareFrom}`}
            color="primary"
            onDelete={() => {
              setCompareMode(false);
              setCompareFrom(null);
            }}
          />
        )}
      </Box>

      <List>
        {versions.map((version, index) => (
          <React.Fragment key={version.version}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1">
                      Version {version.version}
                    </Typography>
                    {version.is_current && (
                      <Chip label="Current" size="small" color="primary" />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      • {formatFileSize(version.size)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {version.version_notes}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Uploaded by {version.uploader_name} •{' '}
                      {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')} (
                      {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })})
                    </Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Download">
                  <IconButton
                    edge="end"
                    onClick={() => handleDownloadVersion(version)}
                    size="small"
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {!version.is_current && (
                  <Tooltip title="Restore">
                    <IconButton
                      edge="end"
                      onClick={() => {
                        setSelectedVersion(version);
                        setRestoreDialogOpen(true);
                      }}
                      size="small"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  edge="end"
                  onClick={(e) => handleMenuOpen(e, version)}
                  size="small"
                >
                  <MoreVertIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
            {index < versions.length - 1 && <Divider component="li" />}
          </React.Fragment>
        ))}
      </List>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore Version {selectedVersion?.version}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Are you sure you want to restore this version? This will create a new version with the content from
            version {selectedVersion?.version}.
          </Typography>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Restoration Notes (optional):
            </Typography>
            <textarea
              value={restoreNotes}
              onChange={(e) => setRestoreNotes(e.target.value)}
              placeholder="Add notes about why you're restoring this version..."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
              }}
            />
          </Box>
          {selectedVersion && (
            <Box mt={2} p={1} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="caption" display="block">
                <strong>Version Details:</strong>
              </Typography>
              <Typography variant="caption" display="block">
                Title: {selectedVersion.title}
              </Typography>
              <Typography variant="caption" display="block">
                Uploaded: {format(new Date(selectedVersion.created_at), 'MMM d, yyyy h:mm a')}
              </Typography>
              <Typography variant="caption" display="block">
                By: {selectedVersion.uploader_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            Cancel
          </Button>
          <Button
            onClick={handleRestoreVersion}
            variant="contained"
            color="primary"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            {restoring ? 'Restoring...' : 'Restore Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuVersion) {
              setCompareMode(true);
              setCompareFrom(menuVersion.version);
              handleMenuClose();
            }
          }}
        >
          <CompareIcon fontSize="small" sx={{ mr: 1 }} />
          Compare with another version
        </MenuItem>
        {compareMode && compareFrom !== menuVersion?.version && (
          <MenuItem onClick={handleCompareVersions}>
            <CompareIcon fontSize="small" sx={{ mr: 1 }} />
            Compare v{compareFrom} with v{menuVersion?.version}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menuVersion) {
              window.open(`/api/documents/${documentId}/versions/${menuVersion.version}`, '_blank');
              handleMenuClose();
            }
          }}
        >
          <InfoIcon fontSize="small" sx={{ mr: 1 }} />
          View version details
        </MenuItem>
        {!menuVersion?.is_current && (
          <MenuItem
            onClick={() => {
              if (menuVersion) {
                handleDeleteVersion(menuVersion);
                handleMenuClose();
              }
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Archive version
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export default DocumentVersionHistory;
